import type { TFunction } from "i18next";
import type {
  FloorPlanOpeningTooltip,
  FloorPlanRoomTooltip,
  FloorPlanWallTooltip,
  FloorPlanZoneTooltip,
} from "../../types";
import type {
  DescribeTooltip,
  DescribeTooltipArgs,
  GetLayoutTooltipContent,
  MeasurementLine,
} from "./types";

const KEY_PREFIX = "smartSurveyor.layoutTooltip";
const FACT_SEPARATOR = " · ";
const ROOM_SEPARATOR = ", ";
const SIDE_SEPARATOR = " ↔ ";

const label = (t: TFunction, key: string) => t(`${KEY_PREFIX}.${key}`);

const formatArea = (areaM2: number) => `${areaM2.toFixed(1)} m²`;
const formatLength = (meters: number) => `${meters.toFixed(2)} m`;
const formatSignedArea = (areaM2: number) =>
  `${areaM2 >= 0 ? "+" : ""}${formatArea(areaM2)}`;

// Unknown values and empty texts are left out of the card rather than shown as a dash.
const present = (items: (string | null)[]): string[] =>
  items.filter((item): item is string => item !== null && item !== "");

const measurementLine: MeasurementLine = ({ t, key, value, format }) =>
  value === null ? null : `${label(t, key)} ${format(value)}`;

const describeZone: DescribeTooltip<FloorPlanZoneTooltip> = ({
  tooltip,
  t,
}) => ({
  title: tooltip.name,
  lines: present([
    present([
      t(`smartSurveyor.zones.kind.${tooltip.zoneKind}`),
      t(`${KEY_PREFIX}.roomsCount`, { count: tooltip.roomNames.length }),
      tooltip.areaM2 === null ? null : formatArea(tooltip.areaM2),
    ]).join(FACT_SEPARATOR),
    tooltip.roomNames.join(ROOM_SEPARATOR),
    // NOTE: the summary is kept out of the card for now; its language is not fixed yet.
  ]),
});

// "printed 35.8 m² (+1.0 m²)": the difference is shown only when the measured area is known.
const printedAreaLine = ({
  tooltip,
  t,
}: DescribeTooltipArgs<FloorPlanRoomTooltip>) => {
  const printed = measurementLine({
    t,
    key: "printed",
    value: tooltip.statedAreaM2,
    format: formatArea,
  });
  if (
    printed === null ||
    tooltip.areaM2 === null ||
    tooltip.statedAreaM2 === null
  ) {
    return printed;
  }
  return `${printed} (${formatSignedArea(tooltip.areaM2 - tooltip.statedAreaM2)})`;
};

const describeRoom: DescribeTooltip<FloorPlanRoomTooltip> = ({
  tooltip,
  t,
}) => ({
  title: tooltip.name,
  lines: present([
    measurementLine({
      t,
      key: "area",
      value: tooltip.areaM2,
      format: formatArea,
    }),
    printedAreaLine({ tooltip, t }),
    `${label(t, "zone")}: ${tooltip.zoneName ?? label(t, "noZone")}`,
  ]),
});

const describeWall: DescribeTooltip<FloorPlanWallTooltip> = ({
  tooltip,
  t,
}) => ({
  title: label(t, "wall"),
  lines: present([
    present([
      measurementLine({
        t,
        key: "length",
        value: tooltip.lengthM,
        format: formatLength,
      }),
      measurementLine({
        t,
        key: "thickness",
        value: tooltip.thicknessM,
        format: formatLength,
      }),
    ]).join(FACT_SEPARATOR),
  ]),
});

const describeOpening: DescribeTooltip<FloorPlanOpeningTooltip> = ({
  tooltip,
  t,
}) => ({
  title: label(t, tooltip.openingKind),
  lines: present([
    measurementLine({
      t,
      key: "width",
      value: tooltip.widthM,
      format: formatLength,
    }),
    tooltip.roomNames
      .map((roomName) => roomName ?? label(t, "outside"))
      .join(SIDE_SEPARATOR),
  ]),
});

// The same lines as the agent's layout viewer.
export const getLayoutTooltipContent: GetLayoutTooltipContent = ({
  tooltip,
  t,
}) => {
  switch (tooltip.kind) {
    case "zone":
      return describeZone({ tooltip, t });
    case "room":
      return describeRoom({ tooltip, t });
    case "wall":
      return describeWall({ tooltip, t });
    case "opening":
      return describeOpening({ tooltip, t });
  }
};
